import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

/**
 * Generates a PDF from HTML content while preserving the exact UI appearance
 * @param {HTMLElement} element - The HTML element to convert to PDF
 * @param {string} filename - The name of the PDF file
 * @param {Object} options - Additional options for PDF generation
 */
export const generatePDFFromHTML = async (
  element,
  filename = 'form-submission.pdf',
  options = {}
) => {
  try {
    // Validate element
    if (!element || !element.nodeType) {
      throw new Error('Invalid element provided')
    }

    // Default options
    const defaultOptions = {
      scale: 1.5, // Optimal scale for quality vs performance
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false, // Disable logging to reduce console noise
      foreignObjectRendering: true, // Better for complex layouts
      imageTimeout: 15000, // Longer timeout for images
      removeContainer: true,
      ...options,
    }

    console.log('Starting PDF generation for element:', element)

    // Create canvas from HTML element
    const canvas = await html2canvas(element, defaultOptions)

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Failed to create canvas or canvas is empty')
    }

    const imgData = canvas.toDataURL('image/png', 1.0)

    // Calculate PDF dimensions
    const imgWidth = canvas.width
    const imgHeight = canvas.height
    const ratio = imgHeight / imgWidth

    // A4 page dimensions in mm
    const pageWidth = 210
    const pageHeight = 297
    const margin = 10

    // Calculate content dimensions
    const contentWidth = pageWidth - margin * 2
    const contentHeight = contentWidth * ratio

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4')

    // If content fits on one page
    if (contentHeight <= pageHeight - margin * 2) {
      pdf.addImage(imgData, 'PNG', margin, margin, contentWidth, contentHeight)
    } else {
      // Multiple pages needed
      let remainingHeight = contentHeight
      let position = 0

      while (remainingHeight > 0) {
        const pageContentHeight = Math.min(
          remainingHeight,
          pageHeight - margin * 2
        )

        // Create a cropped version of the image for this page
        const croppedCanvas = document.createElement('canvas')
        const croppedCtx = croppedCanvas.getContext('2d')

        croppedCanvas.width = canvas.width
        croppedCanvas.height =
          (pageContentHeight / contentHeight) * canvas.height

        croppedCtx.drawImage(
          canvas,
          0,
          (position / contentHeight) * canvas.height,
          canvas.width,
          croppedCanvas.height,
          0,
          0,
          canvas.width,
          croppedCanvas.height
        )

        const croppedImgData = croppedCanvas.toDataURL('image/png', 1.0)

        if (position > 0) {
          pdf.addPage()
        }

        pdf.addImage(
          croppedImgData,
          'PNG',
          margin,
          margin,
          contentWidth,
          pageContentHeight
        )

        remainingHeight -= pageContentHeight
        position += pageContentHeight
      }
    }

    // Save the PDF
    pdf.save(filename)
    console.log('PDF generated successfully:', filename)
    return true
  } catch (error) {
    console.error('Error generating PDF:', error)
    return false
  }
}

/**
 * Generates a PDF specifically for form submissions
 * @param {string} submissionId - The submission ID
 * @param {HTMLElement} formElement - The form element to convert
 */
export const generateFormSubmissionPDF = async (submissionId, formElement) => {
  const filename = `form-submission-${submissionId}-${
    new Date().toISOString().split('T')[0]
  }.pdf`

  // Instead of cloning, let's create a new container and copy all computed styles
  const wrapper = document.createElement('div')
  wrapper.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 210mm;
    min-height: 297mm;
    background: white;
    padding: 20mm;
    font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #000 !important;
    line-height: 1.6;
    box-sizing: border-box;
    z-index: 9999;
    transform: scale(1);
    transform-origin: top left;
  `

  // Add all external stylesheets inline
  const stylesheets = Array.from(document.styleSheets)
  let allStyles = ''

  try {
    stylesheets.forEach((stylesheet) => {
      try {
        if (stylesheet.cssRules) {
          Array.from(stylesheet.cssRules).forEach((rule) => {
            allStyles += rule.cssText + '\n'
          })
        }
      } catch (e) {
        // Cross-origin stylesheets may not be accessible
        console.warn('Could not access stylesheet:', e)
      }
    })
  } catch (e) {
    console.warn('Error accessing stylesheets:', e)
  }

  // Create a style element with all the styles
  const styleElement = document.createElement('style')
  styleElement.textContent =
    allStyles +
    `
    * { 
      box-sizing: border-box; 
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    .form-control-label { 
      font-weight: 600 !important; 
      margin-bottom: 5px !important; 
      display: block !important;
      color: #495057 !important;
    }
    .form-control { 
      border: 1px solid #ced4da !important; 
      border-radius: 0.375rem !important; 
      padding: 0.375rem 0.75rem !important;
      background-color: #fff !important;
      color: #495057 !important;
      display: block !important;
      width: 100% !important;
    }
    .form-group { 
      margin-bottom: 1rem !important; 
    }
    .custom-control-label {
      color: #495057 !important;
      padding-left: 1.5rem !important;
    }
    .custom-control-input:checked ~ .custom-control-label::before {
      background-color: #007bff !important;
      border-color: #007bff !important;
    }
    .border-bottom { 
      border-bottom: 1px solid #dee2e6 !important; 
    }
    .border-dark { 
      border-color: #343a40 !important; 
    }
    h1, h2, h3, h4, h5, h6 { 
      color: #212529 !important; 
      margin-bottom: 0.5rem !important;
    }
    p { 
      margin-bottom: 1rem !important; 
      color: #495057 !important;
    }
    img {
      max-width: 100% !important;
      height: auto !important;
    }
  `

  // Create header
  const header = document.createElement('div')
  header.innerHTML = `
    <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #007bff;">
      <h1 style="color: #007bff; margin: 0; font-size: 28px; font-weight: 600;">Form Submission</h1>
      <p style="margin: 10px 0 0 0; color: #6c757d; font-size: 14px;">Generated on ${new Date().toLocaleDateString()}</p>
    </div>
  `

  // Clone the form content with deep clone including styles
  const clonedContent = formElement.cloneNode(true)

  // Apply inline styles to preserve appearance
  const applyComputedStyles = (originalElement, clonedElement) => {
    if (originalElement.nodeType === Node.ELEMENT_NODE) {
      const computedStyle = window.getComputedStyle(originalElement)
      const importantStyles = [
        'color',
        'background-color',
        'border',
        'border-radius',
        'padding',
        'margin',
        'font-size',
        'font-weight',
        'font-family',
        'display',
        'width',
        'height',
        'text-align',
        'line-height',
      ]

      importantStyles.forEach((prop) => {
        const value = computedStyle.getPropertyValue(prop)
        if (value && value !== 'rgba(0, 0, 0, 0)' && value !== 'transparent') {
          clonedElement.style.setProperty(prop, value, 'important')
        }
      })

      // Recursively apply to children
      for (let i = 0; i < originalElement.children.length; i++) {
        if (clonedElement.children[i]) {
          applyComputedStyles(
            originalElement.children[i],
            clonedElement.children[i]
          )
        }
      }
    }
  }

  // Apply computed styles
  applyComputedStyles(formElement, clonedContent)

  // Assemble the wrapper
  wrapper.appendChild(styleElement)
  wrapper.appendChild(header.firstElementChild)
  wrapper.appendChild(clonedContent)

  // Add to DOM temporarily
  document.body.appendChild(wrapper)

  // Wait for styles to be applied
  await new Promise((resolve) => setTimeout(resolve, 500))

  try {
    const success = await generatePDFFromHTML(wrapper, filename, {
      scale: 1.5,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: true,
      scrollX: 0,
      scrollY: 0,
      width: wrapper.scrollWidth,
      height: wrapper.scrollHeight,
    })

    return success
  } finally {
    // Clean up - remove from DOM
    if (document.body.contains(wrapper)) {
      document.body.removeChild(wrapper)
    }
  }
}

/**
 * Simple fallback PDF generation method
 * @param {string} submissionId - The submission ID
 * @param {HTMLElement} formElement - The form element to convert
 */
export const generateSimplePDF = async (submissionId, formElement) => {
  const filename = `form-submission-${submissionId}-${
    new Date().toISOString().split('T')[0]
  }.pdf`

  try {
    // Create the PDF directly from the existing element without cloning
    const success = await generatePDFFromHTML(formElement, filename, {
      scale: 1,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: true,
      scrollX: 0,
      scrollY: 0,
    })

    return success
  } catch (error) {
    console.error('Simple PDF generation failed:', error)
    return false
  }
}
